/**
 * F-90 import CHUNK PLANNER — pure arithmetic, no session, no Yjs.
 *
 * F-90's requirement is one sentence with two halves that fight each other:
 * import 10k+ rows "without freezing the tab", and make it "one undo step".
 * Freezing is avoided by writing in BOUNDED transactions and yielding to the
 * event loop between them; a Yjs transaction is synchronous, so "bounded" and
 * "one transaction" cannot both be true. This module owns the arithmetic of
 * that split; `import-script.ts` owns the yield and the undo grouping
 * (`BoardSession.beginUndoGroup`).
 */

/**
 * Rows per transaction. F-90 says "~500 rows each", and the measured numbers
 * agree: at 500 rows the bulk `addRows` pass is ~8 ms and 500 `setCell`s are
 * well under a millisecond (2026-07-16 baseline) — inside one frame's budget,
 * so a chunk never drops one. Bigger chunks buy nothing (the Yjs layer is not
 * the bottleneck); smaller ones pay the yield more often.
 */
export const IMPORT_CHUNK_ROWS = 500;

/** A half-open `[start, end)` slice of the parsed rows — one transaction. */
export interface ImportChunk {
  start: number;
  end: number;
}

/**
 * Split `total` rows into `size`-row chunks. Empty for 0 rows: an import of
 * nothing must open no transaction at all, not an empty one (an empty
 * transaction still lands on the undo stack).
 */
export function planChunks(total: number, size: number = IMPORT_CHUNK_ROWS): ImportChunk[] {
  if (total <= 0 || size <= 0) return [];
  const chunks: ImportChunk[] = [];
  for (let start = 0; start < total; start += size) {
    chunks.push({ start, end: Math.min(start + size, total) });
  }
  return chunks;
}

/**
 * How many rows at the END of the sheet an import may WRITE INTO rather than
 * appending after.
 *
 * Why this exists: a new board is seeded 2 columns × 3 EMPTY rows (F-10), and
 * importing a script is the first thing a user does with one. A blind append
 * would leave three blank rows above every imported script on every board — a
 * papercut on the feature's main path.
 *
 * Only TRAILING rows, and only entirely-empty ones: a blank row in the middle
 * of someone's work is a deliberate spacer, and a row with content in any
 * column is data. An import must never be destructive.
 *
 * `rowEmpty` is in sheet order (`rowEmpty[i]` = "row i has no content in any
 * column"); the caller supplies it because emptiness is a doc read, not
 * arithmetic. The scan runs from the end and stops at the first non-empty row
 * or once `needed` is met — so it reads at most `needed` rows of a 10k sheet.
 */
export function reusableTrailingRows(rowEmpty: readonly boolean[], needed: number): number {
  let reusable = 0;
  for (let i = rowEmpty.length - 1; i >= 0 && reusable < needed; i--) {
    if (rowEmpty[i] !== true) break;
    reusable++;
  }
  return reusable;
}

/** 0–100, clamped and integral — the progress bar's width. */
export function importPercent(done: number, total: number): number {
  if (total <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}
